// Messages — one inbox for every supplier↔crew quote conversation across all
// tasks and yachts. Left: searchable thread list grouped by yacht. Right: the
// open conversation with date separators and a rounded composer. Panes swap
// on mobile.
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, Loader2, MessageSquare, Paperclip, Search, Send, Ship, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, API } from "../../lib/api";
import { PageHeader } from "./Dashboard";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return new Date(iso).toLocaleDateString();
}

const AVATAR_PALETTE = [
  "bg-indigo-100 text-indigo-700",
  "bg-rose-100 text-rose-700",
  "bg-emerald-100 text-emerald-700",
  "bg-cyan-100 text-cyan-700",
  "bg-amber-100 text-amber-700",
  "bg-purple-100 text-purple-700",
];

function avatarClass(name) {
  let h = 0;
  for (const c of name || "?") h = (h * 31 + c.charCodeAt(0)) % 997;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function initials(name) {
  const parts = (name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function Avatar({ name, size = "h-10 w-10 text-sm" }) {
  return (
    <span className={`${size} ${avatarClass(name)} rounded-full inline-flex items-center justify-center font-semibold shrink-0 select-none`}>
      {initials(name)}
    </span>
  );
}

const STATUS_CHIPS = {
  pending: ["Pending", "bg-zinc-100 text-zinc-700 border-zinc-200"],
  invited: ["Invited", "bg-zinc-100 text-zinc-700 border-zinc-200"],
  open: ["Open", "bg-zinc-100 text-zinc-700 border-zinc-200"],
  quoted: ["Quoted", "bg-blue-50 text-blue-700 border-blue-200"],
  negotiating: ["Negotiating", "bg-blue-50 text-blue-700 border-blue-200"],
  accepted: ["Quote accepted", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  declined: ["Declined", "bg-red-50 text-red-700 border-red-200"],
  rejected: ["Rejected", "bg-red-50 text-red-700 border-red-200"],
  cancelled: ["Cancelled", "bg-zinc-100 text-zinc-500 border-zinc-200"],
};

function StatusChip({ status, testid }) {
  const [label, cls] = STATUS_CHIPS[status] || (status ? [status, "bg-zinc-100 text-zinc-700 border-zinc-200"] : []);
  if (!label) return null;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${cls}`} data-testid={testid}>
      {label}
    </span>
  );
}

function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(today);
  yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yest.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: d.getFullYear() === today.getFullYear() ? undefined : "numeric" });
}

function ThreadRow({ item, active, onClick, onHide }) {
  const counterpart = item.target === "crew" ? item.supplier_name : item.yacht_name;
  const unread = item.unread > 0;
  return (
    <div className={`group relative w-[calc(100%-16px)] mx-2 my-1 rounded-xl transition-colors duration-200 ${
      active ? "bg-zinc-100 shadow-sm" : unread ? "bg-white shadow-sm ring-1 ring-zinc-200/60 hover:bg-zinc-50" : "hover:bg-zinc-100/70"}`}>
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left p-3 rounded-xl flex gap-3"
      data-testid={`msg-thread-${item.quote_id}`}
    >
      <Avatar name={counterpart || "Conversation"} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm truncate ${unread ? "font-semibold text-zinc-900" : "font-medium text-zinc-800"}`}>
            {counterpart || "Conversation"}
          </span>
          <span className="text-[10px] text-zinc-400 shrink-0">{timeAgo(item.at)}</span>
        </div>
        <div className="text-xs text-zinc-500 truncate">{item.task_title}</div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span className={`text-[11px] truncate ${unread ? "text-zinc-600 font-medium" : "text-zinc-400"}`}>
            {item.sender_name ? `${item.sender_name}: ` : ""}{item.text || (item.kind === "dm" && !item.message_count ? "New conversation" : "(attachment)")}
          </span>
          {unread && (
            <span
              className="shrink-0 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full bg-zinc-900 text-white text-[10px] font-bold"
              data-testid={`msg-thread-unread-${item.quote_id}`}
            >
              {item.unread}
            </span>
          )}
        </div>
      </div>
    </button>
      <span
        role="button"
        tabIndex={0}
        aria-label="Hide conversation from my inbox"
        title="Hide conversation from my inbox"
        onClick={(e) => { e.stopPropagation(); onHide(); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onHide(); } }}
        className="absolute right-2 top-2 p-1 rounded-full bg-white border border-zinc-200 shadow-sm text-zinc-400 hover:text-zinc-900 opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`msg-thread-hide-${item.quote_id}`}
      >
        <X className="h-3 w-3" />
      </span>
    </div>
  );
}

function DateSeparator({ label }) {
  return (
    <div className="flex justify-center my-5" data-testid={`msg-date-sep-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <span className="bg-zinc-100 text-zinc-500 text-xs font-medium px-3 py-1 rounded-full border border-zinc-200/60 shadow-sm">
        {label}
      </span>
    </div>
  );
}

function AttachmentView({ a }) {
  const url = `${API}/files/${a.path}`;
  const isImage = (a.content_type || "").startsWith("image/");
  if (isImage) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block" data-testid={`msg-att-img-${a.path.slice(-8)}`}>
        <img src={url} alt={a.name || "attachment"} className="max-h-48 max-w-full rounded-lg border border-black/10 object-cover" />
      </a>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 text-xs underline underline-offset-2 opacity-90 hover:opacity-100"
      data-testid={`msg-att-file-${a.path.slice(-8)}`}
    >
      <Paperclip className="h-3 w-3 shrink-0" />{a.name || "Attachment"}
    </a>
  );
}

function Bubble({ m, mine, canRemove, onRemove }) {
  if (m.deleted) {
    return (
      <div className={`w-fit max-w-[85%] md:max-w-[70%] rounded-2xl px-4 py-2.5 border border-dashed border-zinc-300 text-zinc-500 italic text-sm bg-zinc-50 ${mine ? "ml-auto" : "mr-auto"}`} data-testid={`msg-removed-${m.id}`}>
        Message removed
      </div>
    );
  }
  return (
    <div className={`w-fit max-w-[85%] md:max-w-[70%] px-4 py-2.5 text-[15px] leading-relaxed shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300 ${
      mine
        ? "ml-auto rounded-2xl rounded-tr-sm bg-zinc-900 text-white"
        : "mr-auto rounded-2xl rounded-tl-sm bg-zinc-100 text-zinc-900 border border-zinc-200/40"}`}>
      <div className={`text-[10px] mb-0.5 flex items-center gap-2 ${mine ? "text-white/60" : "text-zinc-400"}`}>
        <span>{m.sender_name} · {timeAgo(m.at)}</span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="underline underline-offset-2 hover:opacity-80"
            title="Remove this message (within 15 minutes of sending)"
            data-testid={`msg-remove-${m.id}`}
          >
            Remove
          </button>
        )}
      </div>
      <div className="whitespace-pre-wrap break-words">{m.text}</div>
      {Array.isArray(m.attachments) && m.attachments.filter((a) => a && a.path).length > 0 && (
        <div className="mt-1.5 space-y-1.5">
          {m.attachments.filter((a) => a && a.path).map((a) => <AttachmentView key={a.path} a={a} />)}
        </div>
      )}
    </div>
  );
}

function Conversation({ item, onBack, onRead }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingAtts, setPendingAtts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const isDM = item.kind === "dm";
  // "Me" side: crew view → requester messages are mine; supplier view → supplier's.
  const mine = item.target === "crew" ? "requester" : "supplier";
  const base = isDM ? `/direct-threads/${item.quote_id}` : `/quotes/${item.quote_id}`;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(base)
      .then(({ data }) => { if (alive) setQuote(data); })
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => { if (alive) setLoading(false); });
    if (item.unread > 0) {
      api.post(`${base}/mark-read`).then(() => {
        onRead?.(item.quote_id);
        window.dispatchEvent(new Event("seavanto:badges-refresh"));
      }).catch(() => {});
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.quote_id]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [quote?.messages?.length, loading]);

  const send = async () => {
    if ((!msg.trim() && pendingAtts.length === 0) || busy || uploading) return;
    setBusy(true);
    try {
      const payload = { text: msg.trim() };
      if (isDM && pendingAtts.length) payload.attachments = pendingAtts;
      const { data } = await api.post(`${base}/messages`, payload);
      setQuote(data);
      setMsg("");
      setPendingAtts([]);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  // DM attachments: photos & PDFs, uploaded to the thread's yacht under "dm".
  const pickFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files.slice(0, 5)) {
        const fd = new FormData();
        fd.append("file", f);
        if (item.yacht_id) fd.append("yacht_id", item.yacht_id);
        fd.append("folder", "dm");
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPendingAtts((prev) => [...prev, { path: data.storage_path, name: data.original_filename || f.name, content_type: data.content_type || f.type }]);
      }
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setUploading(false); }
  };

  // Sender-side undo: remove own message within 15 min, never once accepted.
  const canRemove = (m) => !isDM && m.sender_kind === mine && !m.deleted && quote?.status !== "accepted"
    && Date.now() - new Date(m.at).getTime() < 15 * 60 * 1000;
  const removeMsg = async (mid) => {
    try {
      const { data } = await api.delete(`/quotes/${item.quote_id}/messages/${mid}`);
      setQuote(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const counterpart = isDM ? item.supplier_name : (item.target === "crew" ? item.supplier_name : item.yacht_name);
  const status = quote?.status || item.status;

  // Group messages by calendar day for "Today / Yesterday / date" separators.
  const dayGroups = useMemo(() => {
    const out = [];
    for (const m of quote?.messages || []) {
      const label = dayLabel(m.at);
      if (!out.length || out[out.length - 1].label !== label) out.push({ label, msgs: [] });
      out[out.length - 1].msgs.push(m);
    }
    return out;
  }, [quote?.messages]);

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="msg-conversation">
      <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-zinc-200 px-4 py-3 bg-white/85 backdrop-blur-xl shadow-sm">
        <button type="button" className="md:hidden text-zinc-500 p-1 -ml-1" onClick={onBack} aria-label="Back to conversations" data-testid="msg-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <Avatar name={counterpart || "Conversation"} size="h-9 w-9 text-xs" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-zinc-900 truncate">{counterpart || "Conversation"}</span>
            <StatusChip status={status} testid="msg-status-chip" />
          </div>
          <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1">
            <Ship className="h-3 w-3 shrink-0" />{item.yacht_name} · {item.task_title}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 bg-white" data-testid="msg-scroll-area">
        {loading && <div className="py-10 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>}
        {!loading && (quote?.messages || []).length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-zinc-400 space-y-3">
            <MessageSquare className="h-8 w-8 text-zinc-300" />
            <p className="text-xs">No messages yet — start the conversation.</p>
          </div>
        )}
        {!loading && dayGroups.map((g) => (
          <div key={g.label}>
            <DateSeparator label={g.label} />
            <div className="space-y-2">
              {g.msgs.map((m) => (
                <Bubble key={m.id} m={m} mine={isDM ? m.mine : m.sender_kind === mine} canRemove={canRemove(m)} onRemove={() => removeMsg(m.id)} />
              ))}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="p-3 sm:p-4 bg-white border-t border-zinc-200">
        {isDM && pendingAtts.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="msg-pending-atts">
            {pendingAtts.map((a, i) => (
              <span key={a.path} className="inline-flex items-center gap-1.5 text-xs bg-zinc-100 border border-zinc-200 rounded-full pl-2.5 pr-1 py-1">
                <Paperclip className="h-3 w-3 text-zinc-400" />
                <span className="max-w-[160px] truncate">{a.name}</span>
                <button
                  type="button"
                  onClick={() => setPendingAtts((prev) => prev.filter((_, j) => j !== i))}
                  className="p-0.5 rounded-full hover:bg-zinc-200"
                  aria-label={`Remove ${a.name}`}
                  data-testid={`msg-pending-att-remove-${i}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 bg-zinc-50 rounded-3xl p-1.5 border border-zinc-200 focus-within:ring-2 focus-within:ring-zinc-900 focus-within:border-transparent shadow-sm transition-shadow">
          {isDM && (
            <>
              <input ref={fileRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={pickFiles} data-testid="msg-attach-input" />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                aria-label="Attach a photo or PDF"
                title="Attach a photo or PDF"
                className="h-9 w-9 rounded-full text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200/60 flex items-center justify-center shrink-0 transition-colors disabled:opacity-40"
                data-testid="msg-attach-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              </button>
            </>
          )}
          <input
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type a reply…"
            className="flex-1 bg-transparent border-none outline-none focus:ring-0 py-2 px-3 text-sm text-zinc-900 placeholder:text-zinc-400"
            data-testid="msg-reply-input"
          />
          <button
            type="button"
            onClick={send}
            disabled={busy || uploading || (!msg.trim() && pendingAtts.length === 0)}
            aria-label="Send message"
            className="h-9 w-9 rounded-full bg-zinc-900 text-white flex items-center justify-center hover:bg-zinc-800 shrink-0 transition-transform active:scale-95 disabled:opacity-40 disabled:active:scale-100"
            data-testid="msg-reply-send"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function YachtGroup({ group, activeId, onOpen, onHide }) {
  const [open, setOpen] = useState(true);
  const unreadTotal = group.threads.reduce((s, t) => s + (t.unread || 0), 0);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="sticky top-0 z-10 w-full flex items-center justify-between gap-2 bg-zinc-50/95 backdrop-blur border-b border-zinc-200/60 px-4 py-2 select-none"
        data-testid={`msg-yacht-group-${group.threads[0]?.yacht_id || "x"}`}
      >
        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-500 min-w-0">
          <Ship className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{group.name}</span>
        </span>
        <span className="flex items-center gap-1.5 shrink-0">
          {unreadTotal > 0 && !open && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full bg-zinc-900 text-white text-[9px] font-bold">
              {unreadTotal}
            </span>
          )}
          <ChevronDown className={`h-3.5 w-3.5 text-zinc-400 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
        </span>
      </button>
      {open && group.threads.map((it) => (
        <ThreadRow key={it.quote_id} item={it} active={it.quote_id === activeId} onClick={() => onOpen(it.quote_id)} onHide={() => onHide(it.quote_id)} />
      ))}
    </div>
  );
}

export default function Messages() {
  const [items, setItems] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get(`/messages/inbox`);
      setItems(data || []);
    } catch (e) { setItems([]); toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  // Deep-link: ?dm=<thread_id> auto-opens that direct-message conversation
  // (used by the "Message" button on supplier rows).
  useEffect(() => {
    const dm = searchParams.get("dm");
    if (!dm || items === null) return;
    if (items.some((i) => i.quote_id === dm)) setActiveId(dm);
    const p = new URLSearchParams(searchParams);
    p.delete("dm");
    setSearchParams(p, { replace: true });
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const markLocalRead = (quoteId) =>
    setItems((prev) => (prev || []).map((i) => (i.quote_id === quoteId ? { ...i, unread: 0 } : i)));

  // "Delete for me": hide the thread from MY inbox only — it resurfaces
  // automatically when the other party writes again.
  const hideThread = async (quoteId) => {
    try {
      await api.post(`/messages/inbox/${quoteId}/hide`);
      setItems((prev) => (prev || []).filter((i) => i.quote_id !== quoteId));
      if (activeId === quoteId) setActiveId(null);
      toast.success("Conversation hidden — it comes back if a new message arrives");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const active = (items || []).find((i) => i.quote_id === activeId) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items || [];
    return (items || []).filter((i) =>
      [i.supplier_name, i.yacht_name, i.task_title, i.text, i.sender_name]
        .some((v) => (v || "").toLowerCase().includes(q)));
  }, [items, query]);

  // Group threads by yacht → newest conversation first inside each yacht;
  // yacht groups ordered by their newest conversation overall.
  const groups = useMemo(() => {
    const map = new Map();
    for (const it of filtered) {
      const key = it.yacht_id || it.yacht_name || "—";
      if (!map.has(key)) map.set(key, { name: it.yacht_name || "Yacht", threads: [] });
      map.get(key).threads.push(it);
    }
    const out = [...map.values()];
    for (const g of out) g.threads.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
    out.sort((a, b) => (b.threads[0]?.at || "").localeCompare(a.threads[0]?.at || ""));
    return out;
  }, [filtered]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 h-full flex flex-col" data-testid="messages-page">
      <PageHeader overline="Inbox" title="Messages" />
      <div className="flex-1 min-h-0 min-w-0 grid grid-cols-1 md:grid-cols-[340px_1fr] rounded-xl border border-zinc-200 bg-white overflow-hidden shadow-sm" style={{ minHeight: "60vh" }}>
        {/* Thread list — hidden on mobile when a thread is open */}
        <div className={`min-w-0 border-r border-zinc-200 bg-zinc-50/40 flex-col min-h-0 ${active ? "hidden md:flex" : "flex"}`} data-testid="msg-thread-list">
          <div className="p-3 border-b border-zinc-200 bg-zinc-50/80 backdrop-blur-md">
            <div className="relative">
              <Search className="h-4 w-4 text-zinc-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search conversations…"
                className="w-full rounded-full bg-white border border-zinc-200 shadow-sm pl-10 pr-4 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-shadow"
                data-testid="msg-search-input"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {items === null && <div className="py-10 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>}
            {items !== null && items.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center text-sm text-zinc-500 px-6 space-y-2" data-testid="msg-empty">
                <MessageSquare className="h-8 w-8 text-zinc-300" />
                <span>No conversations yet. Messages with suppliers on your tasks show up here.</span>
              </div>
            )}
            {items !== null && items.length > 0 && filtered.length === 0 && (
              <div className="py-10 text-center text-sm text-zinc-400 px-6" data-testid="msg-search-empty">
                No conversations match “{query}”.
              </div>
            )}
            {groups.map((g) => (
              <YachtGroup key={g.name + (g.threads[0]?.quote_id || "")} group={g} activeId={activeId} onOpen={setActiveId} onHide={hideThread} />
            ))}
          </div>
        </div>
        {/* Conversation pane */}
        <div className={`${active ? "" : "hidden md:flex"} flex-col min-h-0 min-w-0 ${active ? "flex" : ""}`}>
          {active ? (
            <Conversation item={active} onBack={() => setActiveId(null)} onRead={markLocalRead} />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-zinc-400 bg-zinc-50/30" data-testid="msg-none-selected">
              <span className="h-14 w-14 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-zinc-300" />
              </span>
              <span className="text-sm">Select a conversation</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
